import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BookOpen, X, ArrowRight, Lock, BookMarked, Loader2, Sparkles } from "lucide-react";
import { FREE_STORY, PAPERBACK_URL, KINDLE_URL } from "../site-data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FreeStory() {
  const [open, setOpen] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const [email, setEmail] = useState("");
  const [honey, setHoney] = useState("");
  const [loading, setLoading] = useState(false);
  const renderedAt = useRef(Date.now());
  const dialogRef = useRef(null);

  // lock body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  // focus close button on open + reset rendered timer
  useEffect(() => {
    if (open) {
      renderedAt.current = Date.now();
      setTimeout(() => dialogRef.current?.focus(), 50);
    }
  }, [open]);

  // restore unlock status from localStorage (convenience for returning reader)
  useEffect(() => {
    if (localStorage.getItem("ns_story_unlocked") === "1") setUnlocked(true);
  }, []);

  // close on Esc
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const handleUnlock = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email to continue.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, {
        email,
        source: "story_unlock",
        company_website: honey,
        elapsed_ms: Date.now() - renderedAt.current,
      });
      localStorage.setItem("ns_story_unlocked", "1");
      setUnlocked(true);
      toast.success("Unlocked. The story continues below.");
      // smooth scroll inside the modal to the unlocked section
      setTimeout(() => {
        document.getElementById("story-unlocked-anchor")?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not continue. Please check your email.");
    } finally {
      setLoading(false);
    }
  };

  const honeyStyle = {
    position: "absolute", left: "-9999px", top: "auto",
    width: "1px", height: "1px", overflow: "hidden",
  };

  return (
    <section
      id="free-story"
      data-testid="free-story-section"
      className="relative py-24 lg:py-36 bg-white overflow-hidden"
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <p className="eyebrow mb-5">
              <Sparkles className="inline mr-2 -mt-1" size={14} />
              A Gift, before the Book
            </p>
            <h2
              className="font-serif text-4xl lg:text-5xl tracking-tight leading-[1.05] text-[#1c1a1a]"
              data-testid="free-story-title"
            >
              Read a <span className="italic text-[#8b2e3e]">free short story</span> — your first taste of
              the collection.
            </h2>
            <p className="mt-6 text-[#4a4646] text-lg leading-relaxed max-w-xl">
              <span className="font-serif italic text-[#1c1a1a]">Smell a Rose.</span> Here’s a brief glimpse from one of the stories 
              in <span className="font-serif italic text-2xl text-[#1c1a1a]">A Bouquet of 8 Roses</span>. It takes just a few minutes - and if it stays with you, the rest of the book is waiting.
            </p>

            <button
              type="button"
              onClick={() => setOpen(true)}
              data-testid="free-story-open-btn"
              className="btn-rose mt-10 inline-flex items-center gap-2 bg-[#8b2e3e] hover:bg-[#6d2230] text-white px-7 py-4 text-sm tracking-[0.15em] uppercase transition-colors"
            >
              <BookOpen size={16} /> Open the story
            </button>

            <ul className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm text-[#4a4646] border-t border-[#e5dcd0] pt-5 max-w-xl">
              <li>· {FREE_STORY.readTime}</li>
              <li>· No spam — just occasional notes</li>
              <li>· Unsubscribe anytime</li>
            </ul>
          </div>

          <div className="lg:col-span-5">
            <div className="relative mx-auto max-w-sm">
              <div className="absolute -inset-4 bg-[#f4d8d8]/40 rotate-3" aria-hidden />
              <div className="relative bg-[#f9f6f0] border border-[#e5dcd0] p-8 book-shadow">
                <p className="eyebrow !text-[#8b2e3e]">Free story</p>
                <h3 className="font-serif text-3xl text-[#1c1a1a] mt-3 leading-tight">
                  {FREE_STORY.title}
                </h3>
                <p className="font-serif italic text-[#4a4646] mt-2">{FREE_STORY.subtitle}</p>

                <div className="mt-8 space-y-2 text-sm text-[#4a4646]">
                  <p className="flex items-center gap-2">
                    <span className="h-1 w-1 rounded-full bg-[#8b2e3e] inline-block" />
                    A young boy. A usual day. A lost wallet.
                  </p>
                  <p className="flex items-center gap-2">
                    <span className="h-1 w-1 rounded-full bg-[#8b2e3e] inline-block" />
                    A choice - small, and not small at all.
                  </p>
                  <p className="flex items-center gap-2">
                    <span className="h-1 w-1 rounded-full bg-[#8b2e3e] inline-block" />
                    Determination — and the life it changed.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="story-modal-title"
          data-testid="free-story-modal"
          className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-8"
        >
          {/* backdrop */}
          <div
            className="absolute inset-0 bg-[#1c1a1a]/70 backdrop-blur-sm"
            onClick={() => setOpen(false)}
            aria-hidden
          />

          <div
            ref={dialogRef}
            tabIndex={-1}
            className="relative bg-[#f9f6f0] border border-[#e5dcd0] w-full max-w-2xl max-h-[92vh] overflow-y-auto paper-bg"
            data-testid="free-story-dialog"
          >
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close"
              data-testid="free-story-close"
              className="sticky top-3 float-right mr-3 p-2 text-[#4a4646] hover:text-[#8b2e3e] bg-white/80 border border-[#e5dcd0]"
            >
              <X size={16} />
            </button>

            <article className="px-7 sm:px-12 py-12">
              <p className="eyebrow !text-[#8b2e3e]">
                <Sparkles className="inline mr-2 -mt-1" size={12} /> Bonus story · {FREE_STORY.readTime}
              </p>
              <h2
                id="story-modal-title"
                className="font-serif text-4xl sm:text-5xl text-[#1c1a1a] tracking-tight mt-3 leading-[1.05]"
              >
                {FREE_STORY.title}
              </h2>
              <p className="font-serif italic text-[#4a4646] mt-3">{FREE_STORY.subtitle}</p>

              <p className="mt-8 font-serif italic text-lg text-[#8b2e3e] border-l-2 border-[#e5c9c5] pl-5">
                {FREE_STORY.preface}
              </p>

              <div className="mt-10 space-y-5 text-[#1c1a1a] leading-[1.8] text-[17px] font-serif">
                {FREE_STORY.part1.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>

              <div id="story-unlocked-anchor" />

              {!unlocked ? (
                <div
                  data-testid="free-story-gate"
                  className="mt-12 relative"
                >
                  <div className="absolute -top-14 inset-x-0 h-14 bg-gradient-to-b from-transparent to-[#f9f6f0] pointer-events-none" />
                  <div className="border border-[#e5c9c5] bg-white p-7 sm:p-9">
                    <div className="flex items-center gap-3 text-[#8b2e3e]">
                      <Lock size={16} />
                      <p className="eyebrow !text-[#8b2e3e]">The rest of the story</p>
                    </div>
                    <h3 className="font-serif text-2xl sm:text-3xl text-[#1c1a1a] mt-3 leading-tight">
                      Enter your email to continue reading.
                    </h3>
                    <p className="text-[#4a4646] mt-3 leading-relaxed">
                      We'll send you the occasional note when a new story arrives. No spam, no noise —
                      and you're free to unsubscribe any time.
                    </p>

                    <form onSubmit={handleUnlock} className="mt-6 flex flex-col sm:flex-row gap-3" data-testid="free-story-form">
                      <input
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="your@email.com"
                        autoComplete="email"
                        data-testid="free-story-email"
                        className="flex-1 bg-white border border-[#e5dcd0] px-4 py-3 text-base focus:outline-none focus:border-[#8b2e3e] focus:ring-2 focus:ring-[#e5c9c5]"
                      />
                      {/* honeypot */}
                      <label style={honeyStyle} aria-hidden="true">
                        <span>Company website</span>
                        <input
                          type="text"
                          tabIndex={-1}
                          autoComplete="off"
                          name="company_website"
                          value={honey}
                          onChange={(e) => setHoney(e.target.value)}
                        />
                      </label>
                      <button
                        type="submit"
                        disabled={loading}
                        data-testid="free-story-submit"
                        className="btn-rose inline-flex items-center justify-center gap-2 bg-[#8b2e3e] hover:bg-[#6d2230] text-white px-6 py-3 text-sm tracking-[0.15em] uppercase transition-colors disabled:opacity-60"
                      >
                        {loading ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
                        Continue reading
                      </button>
                    </form>

                    <p className="mt-4 text-xs text-[#4a4646]">
                      By continuing, you join Nitin's reader list. Your email stays private.
                    </p>
                  </div>
                </div>
              ) : (
                <div data-testid="free-story-unlocked">
                  <div className="mt-12 space-y-5 text-[#1c1a1a] leading-[1.8] text-[17px] font-serif">
                    {FREE_STORY.part2.map((p, i) => (
                      <p key={i}>{p}</p>
                    ))}
                  </div>

                  <p className="mt-10 font-serif italic text-[#4a4646] text-lg leading-relaxed border-l-2 border-[#8b2e3e] pl-5 whitespace-pre-line">
                    {FREE_STORY.cliff}
                  </p>

                  <div className="mt-10 border-t border-[#e5dcd0] pt-8">
                    <p className="eyebrow !text-[#8b2e3e]">Continue the bouquet</p>
                    <h3 className="font-serif text-2xl sm:text-3xl text-[#1c1a1a] mt-2 leading-snug">
                      {FREE_STORY.cta}
                    </h3>
                    <div className="mt-6 flex flex-wrap gap-3">
                      <a
                        href={PAPERBACK_URL}
                        target="_blank" rel="noopener noreferrer"
                        data-testid="free-story-cta-amazon"
                        className="btn-rose inline-flex items-center gap-2 bg-[#8b2e3e] hover:bg-[#6d2230] text-white px-6 py-3.5 text-sm tracking-[0.15em] uppercase transition-colors"
                      >
                        Paperback · Amazon <ArrowRight size={14} />
                      </a>
                      <a
                        href={KINDLE_URL}
                        target="_blank" rel="noopener noreferrer"
                        data-testid="free-story-cta-kindle"
                        className="inline-flex items-center gap-2 border border-[#1c1a1a] hover:bg-[#1c1a1a] hover:text-white text-[#1c1a1a] px-6 py-3.5 text-sm tracking-[0.15em] uppercase transition-colors"
                      >
                        <BookMarked size={16} /> Read on Kindle
                      </a>
                    </div>
                  </div>
                </div>
              )}
            </article>
          </div>
        </div>
      )}
    </section>
  );
}
